import { Header } from '@/components/header/Header'
import { Tag } from '@/components/tag/Tag'
import { RecentMatterActivity } from '@/matter/components/matter-overview/RecentMatterActivity'
import { MatterOverviewActions } from '@/matter/components/matter-overview/MatterOverviewActions'
import { DraftCarousel } from '@/matter/components/matter-overview/DraftCarousel'
import { Edit, NewChat, Settings, TrashDelete, Upload } from '@/assets/icons'
import { useMatterOverview } from '@/matter/components/matter-overview/useMatterOverview'
import { CollaborationHeaderActions } from '@/collaboration/components/CollaborationHeaderActions'
import { useCollaborationUpsell } from '@/collaboration/hooks/useCollaborationUpsell'
import { Card } from '@/components/card/Card'
import { SampleMatterChecklistCard } from '@/matter/components/matter-overview/SampleMatterChecklistCard'
import { kUnassignedMatterDescription } from '@/matter/constants'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { DEVICE_TYPE, useHeaderDeviceType } from '@/components/header/hooks/useHeaderDeviceType'
import { useDynamicStickyHeaderTitle } from '@/matter/hooks/useDynamicStickyHeaderTitle'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { canCreateChat, canDeleteMatter, canEditMatter, canEditMatterSettings, canViewChats } from '@/matter/utils/matter-permissions'
import { MatterType } from '@/matter/store/schemas'
import { MatterJurisdiction } from '@/matter/components/matter-overview/MatterJurisdiction'

/** Px offset from the top of the scroll container to the title element — controls when the header title appears */
const TITLE_SCROLL_THRESHOLD_PX = 26

export default function MatterOverview() {
  const {
    matter,
    handleEditMatter,
    handleEditMatterJurisdiction,
    handleUploadFiles,
    handleUploadUserFiles,
    handleMatterSettings,
    handleDeleteMatter,
    handleNewChat,
    newFilesUxEnabled,
  } = useMatterOverview()
  const { showUpsell, handleUpsellClick } = useCollaborationUpsell()
  const deviceType = useHeaderDeviceType()
  const isMobile = deviceType !== DEVICE_TYPE.DESKTOP

  const isUnassigned = isUnassignedMatter(matter)
  const isSampleMatter = matter?.type === MatterType.SAMPLE
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const sampleMatterTourEnabled = kGetFeatureFlagStatus(kFeatureFlags.SAMPLE_MATTER_TOUR)
  const title = matter?.name ?? 'Untitled' // All matters will have a name, this is just a defensive fallback
  const client = matter?.client

  // We're overriding the description for the unassigned matter to give the user an actionable suggestion.
  const description = isUnassigned ? kUnassignedMatterDescription : matter?.description

  // Use the display title from above so the matter overview correctly displays the title in the header when scrolled
  const { showTitle, stickyHeaderTitleRef } = useDynamicStickyHeaderTitle({
    title,
    scrollThreshold: TITLE_SCROLL_THRESHOLD_PX,
  })

  if (!matter) return null

  const userCanViewChats = canViewChats(matter)
  const chatDisabled = isUnassigned || !canCreateChat(matter)

  return (
    <div className="flex min-h-0 flex-1 flex-col w-full min-w-0">
      {/* Header */}
      <Header
        titleSize="sm"
        title={isMobile || showTitle ? title : undefined}
        button={{
          label: 'Chat',
          onClick: handleNewChat,
          variant: 'tonal',
          color: 'neutral',
          trailingIcon: <NewChat className={`h-4 w-4 ${chatDisabled ? 'text-true-gray-300' : 'text-brand-neutral-800'}`} />,
          disabled: chatDisabled,
        }}
        menuItems={[
          // Edit matter
          ...(canEditMatter(matter) ? [{ label: 'Edit', icon: Edit, onSelect: handleEditMatter }] : []),
          // Upload files
          newFilesUxEnabled
            ? { label: 'Upload Matter Files', icon: Upload, onSelect: handleUploadUserFiles }
            : { label: 'Upload files', icon: Upload, onSelect: handleUploadFiles },
          // Matter settings
          ...(driveSyncEnabled && canEditMatterSettings(matter) ? [{ label: 'Matter Settings', icon: Settings, onSelect: handleMatterSettings }] : []),
          // Delete matter
          ...(canDeleteMatter(matter) ? [{ label: 'Delete', icon: TrashDelete, onSelect: handleDeleteMatter, destructive: true }] : []),
        ]}
        menuFooter={
          showUpsell
            ? {
                content: (
                  <Card
                    variant="upsell"
                    size="sm"
                    title="Add Team Members"
                    description="Create an organization to collaborate on this matter with colleagues."
                  />
                ),
                onSelect: handleUpsellClick,
              }
            : undefined
        }
        leadingContent={<CollaborationHeaderActions />}
        menuDisabled={isUnassigned}
        menuAriaLabel="Matter actions"
        hideButtonOnMobile // Button is hidden on mobile because it's in the DashboardMobileTopBar instead
        borderBottom={showTitle}
      />

      {/* the x-padding gives the recent-drafts carousel a gutter for its scroll buttons, which sit just outside its track (see Carousel's layout contract). */}
      <div className="flex-1 overflow-y-auto flex flex-col px-2 sm:px-6">
        {/* Main content */}
        <div className="mx-auto w-full max-w-[800px] min-w-0 px-4 pt-6 lg:pt-0 flex flex-col flex-1">
          <div className="px-4">
            {/* 1. Matter Title  */}
            {title && (
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-brand-neutral-900" ref={stickyHeaderTitleRef}>
                  {title}
                </h3>
                {isSampleMatter && (
                  <Tag variant="tonal" color="Green" size="xs">
                    Sample Matter
                  </Tag>
                )}
                {isUnassigned && (
                  <Tag variant="tonal" color="Neutral" size="2xs">
                    to File
                  </Tag>
                )}
              </div>
            )}

            {/* 2. Client */}
            {client && (
              <div className="flex items-center gap-3 mt-1">
                <span className="text-base font-medium text-brand-neutral-700">{client}</span>
              </div>
            )}

            {/* 3. Summary/Description */}
            {description && (
              <div className="mt-4">
                <p className="text-sm leading-relaxed text-brand-neutral-800">{description}</p>
              </div>
            )}

            {/* 4. Jurisdiction */}
            <MatterJurisdiction matter={matter} onEdit={handleEditMatterJurisdiction} />
          </div>

          <div className="px-4">
            {isSampleMatter && sampleMatterTourEnabled && <SampleMatterChecklistCard />}
            <MatterOverviewActions />
            {/* Remount per matter so loading state doesn't carry over between matters */}
            {userCanViewChats && <DraftCarousel key={matter?.id} />}
          </div>

          {userCanViewChats && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <RecentMatterActivity />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
